import { Schema } from "effect";
import { createEmptyCard, fsrs, generatorParameters, Rating as FsrsRating, State, type Card } from "ts-fsrs";
import { LEARNING_STEPS, RELEARNING_STEPS } from "./steps.ts";
import { Reviewed, type CardState, type Instant, type Rating, type Retention } from "./types.ts";

export const KNOWN_STABILITY_DAYS = 7;
export const MATURE_STABILITY_DAYS = 21;

export interface FsrsParams {
  readonly desiredRetention: Retention;
}

export const params = (desiredRetention: Retention): FsrsParams => ({ desiredRetention });

export const known = (s: CardState): boolean => s.tag === "review" && s.memory.stability >= KNOWN_STABILITY_DAYS;
export const mature = (s: CardState): boolean => s.tag === "review" && s.memory.stability >= MATURE_STABILITY_DAYS;

const STATE = { learning: State.Learning, review: State.Review, relearning: State.Relearning } as const;
const TAG = { [State.Learning]: "learning", [State.Review]: "review", [State.Relearning]: "relearning" } as const;
const GRADE = { again: FsrsRating.Again, good: FsrsRating.Good } as const;

const decodeReviewed = Schema.decodeUnknownSync(Reviewed);

function toFsrs(s: CardState, now: Instant): Card {
  if (s.tag === "new") return createEmptyCard(new Date(now));
  return {
    due: new Date(s.dueAt),
    stability: s.memory.stability,
    difficulty: s.memory.difficulty,
    elapsed_days: 0,
    scheduled_days: 0,
    learning_steps: s.tag === "review" ? 0 : s.step,
    reps: s.memory.reps,
    lapses: s.memory.lapses,
    state: STATE[s.tag],
    last_review: new Date(s.memory.lastReviewAt),
  };
}

function fromFsrs(card: Card): Reviewed {
  if (card.state === State.New) throw new Error("ts-fsrs returned a new card after a review");
  const tag = TAG[card.state];
  const memory = {
    stability: card.stability,
    difficulty: card.difficulty,
    lastReviewAt: card.last_review?.getTime(),
    reps: card.reps,
    lapses: card.lapses,
  };
  const dueAt = card.due.getTime();
  return decodeReviewed(tag === "review" ? { tag, memory, dueAt } : { tag, memory, step: card.learning_steps, dueAt });
}

export function review(s: CardState, r: Rating, now: Instant, p: FsrsParams): Reviewed {
  // TODO: Reject now before lastReviewAt; a backward clock must not regress memory or feed negative elapsed time.
  const scheduler = fsrs(
    generatorParameters({
      request_retention: p.desiredRetention,
      enable_fuzz: true,
      learning_steps: LEARNING_STEPS,
      relearning_steps: RELEARNING_STEPS,
    }),
  );
  return fromFsrs(scheduler.next(toFsrs(s, now), new Date(now), GRADE[r]).card);
}
